import { useEffect, useState } from "react";
import { getUnreadMessageCount, subscribeToInboxPings } from "@/lib/messaging";

/**
 * Returns the current unread-message count for the caller. Refreshes
 * on mount + every 60 s + whenever Realtime surfaces a new message
 * anywhere the caller can see it.
 *
 * Designed for nav badges — fails quietly to 0 on any RPC error so a
 * misconfigured migration can't blank the whole shell.
 */
export default function useUnreadMessages(profileId) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!profileId) return;
    let alive = true;

    async function refresh() {
      try {
        const n = await getUnreadMessageCount({ profileId });
        if (alive) setCount(n || 0);
      } catch {
        if (alive) setCount(0);
      }
    }

    refresh();
    const id = setInterval(refresh, 60 * 1000);
    let unsub = () => {};
    try {
      unsub = subscribeToInboxPings(() => refresh()) || (() => {});
    } catch {
      // Never let a realtime subscribe failure crash the shell. The
      // 60s poller still keeps the badge roughly fresh.
      unsub = () => {};
    }
    // Read-state changes (user opened a thread → marked read) aren't
    // surfaced by inbox pings (those fire only on new INSERTs). Listen
    // for the custom event dispatched by markConversationRead so the
    // badge updates instantly instead of waiting for the 60s poll.
    const onReadChange = () => refresh();
    if (typeof window !== "undefined") {
      window.addEventListener("messaging:read-changed", onReadChange);
    }
    return () => {
      alive = false;
      clearInterval(id);
      try { unsub(); } catch { /* ignore */ }
      if (typeof window !== "undefined") {
        window.removeEventListener("messaging:read-changed", onReadChange);
      }
    };
  }, [profileId]);

  return count;
}
